/**
 * Run an async action on each item of an **array** with a concurrency limit.
 *
 * @example
 * ```ts
 * // Setup
 * const data = [1, 2, 3, 4, 5];
 *
 * const ayncAction = async (item: number) => {
 *  await new Promise((resolve) => setTimeout(resolve, 1000)); // sleep - fake async
 *  console.log(item);
 * };
 *
 * // Process all items with a concurrency limit of 2
 * await processWithConcurrency({data, action: ayncAction, limit: 2});
 */
export async function processWithConcurrency<T>({
  data,
  action,
  limit,
}: {
  data: T[];
  action: (item: T) => Promise<void>;
  limit: number;
}): Promise<void> {
  const dataChunks = [];

  for (let i = 0; i < data.length; i += limit) {
    dataChunks.push(data.slice(i, i + limit));
  }
  for (const dataChunk of dataChunks) {
    await Promise.all(dataChunk.map(action));
  }
}
